import { useDealTags, type StageTag } from '@/hooks/useDealTags';
import { cn } from '@/lib/utils';
import { Loader2, Plus, X } from 'lucide-react';
import { useState } from 'react';

interface DealTagsSelectorProps {
  dealId: string;
  currentStage: string;
  className?: string;
}

/**
 * Component to display and manage tags for a deal
 * Shows available tags for the current stage as clickable chips
 */
export function DealTagsSelector({ dealId, currentStage, className }: DealTagsSelectorProps) {
  const {
    availableTags,
    availableTagsLoading,
    assignedTagsLoading,
    isTagAssigned,
    toggleTag,
    isToggling,
  } = useDealTags(dealId, currentStage);

  const [togglingTagId, setTogglingTagId] = useState<string | null>(null);

  const handleToggle = async (tagId: string) => {
    setTogglingTagId(tagId);
    try {
      await toggleTag(tagId);
    } finally {
      setTogglingTagId(null);
    }
  };

  if (availableTagsLoading || assignedTagsLoading) {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Carregando tags...</span>
      </div>
    );
  }

  if (availableTags.length === 0) {
    return null; // No tags available for this stage
  }

  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      {availableTags.map((tag) => (
        <TagChip
          key={tag.id}
          tag={tag}
          isAssigned={isTagAssigned(tag.id)}
          isLoading={togglingTagId === tag.id}
          disabled={isToggling}
          onClick={() => handleToggle(tag.id)}
        />
      ))}
    </div>
  );
}

interface TagChipProps {
  tag: StageTag;
  isAssigned: boolean;
  isLoading: boolean;
  disabled: boolean;
  onClick: () => void;
}

function TagChip({ tag, isAssigned, isLoading, disabled, onClick }: TagChipProps) {
  const color = tag.color || '#6B7280'; // Default gray if no color

  // Generate styles based on assignment state
  const chipStyle = isAssigned
    ? {
        backgroundColor: color,
        borderColor: color,
        color: getContrastColor(color),
      }
    : {
        backgroundColor: 'transparent',
        borderColor: color,
        color: color,
      };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || isLoading}
      className={cn(
        'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium',
        'border transition-all duration-150',
        'hover:scale-105 hover:shadow-sm',
        'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100',
        isAssigned && 'shadow-sm'
      )}
      style={chipStyle}
    >
      {isLoading ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : isAssigned ? (
        <X className="h-3 w-3" />
      ) : (
        <Plus className="h-3 w-3" />
      )}
      <span>{tag.name}</span>
    </button>
  );
}

/**
 * Get contrasting text color (white or black) based on background color
 */
function getContrastColor(hexColor: string): string {
  // Remove # if present
  const hex = hexColor.replace('#', '');
  
  // Parse RGB values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Return white for dark backgrounds, dark for light backgrounds
  return luminance > 0.5 ? '#1f2937' : '#ffffff';
}
